import { Spin } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { useConfirm } from 'sutra/components/confirm/use-confirm'
import { Text } from 'sutra/components/Text/Text'
import { gray } from 'sutra/tokens/colors'
import { spacing } from 'sutra/tokens/spacing'

import { handleException } from 'clients/exception-handling'
import { useFilterService } from 'features/filters/service'
import { useFeatureFlagGate } from 'features/project/feature-flags'
import { useTopBar } from 'features/project/partials/project-layout/use-layout'
import { useProjectStore } from 'features/project/store'
import { AgentSharingModal } from 'features/trend-detection/components/AgentSharingModal/AgentSharingModal'
import {
  ActivityFeed,
  AgentInfoCard,
  DetailHeader,
  NotificationsCard as ReskinNotificationsCard,
} from 'features/trend-detection/features/issue-agent-detail'
import type {
  ActivityFeedRun,
  InsightsRecency,
} from 'features/trend-detection/features/issue-agent-detail'
import {
  ComparisonGroupIcon,
  FrameInspectIcon,
} from 'features/trend-detection/features/issue-agent-detail/components/SectionIcons'
import { useBenchmarkingAgentRuns } from 'features/trend-detection/features/issue-agent-detail/hooks/use-benchmarking-agent-runs'
import { useFilterOptions } from 'features/trend-detection/features/issue-agent-detail/hooks/use-filter-options'
import {
  ResearchAgentsAction,
  trackResearchAgents,
} from 'features/trend-detection/mixpanel'
import {
  trendDetectionAgentCreatePath,
  trendDetectionAgentsPath,
  trendDetectionFeedPath,
} from 'features/trend-detection/paths'
import { useTrendDetectionService } from 'features/trend-detection/service'
import type { BenchmarkingAgentDetail } from 'features/trend-detection/types'
import type { ProjectState } from 'types/project'

const BENCHMARKING_AGENT_FLAG = 'benchmarking_agent'

export const BenchmarkingAgentDetailPage: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>()
  const navigate = useNavigate()
  const projectId = useProjectStore((s: ProjectState) => s.projectId)
  const filterService = useFilterService()
  const trendService = useTrendDetectionService()
  const queryClient = useQueryClient()
  const { setBreadcrumbSubtitle } = useTopBar()

  const [isSharing, setIsSharing] = useState(false)

  useFeatureFlagGate(
    BENCHMARKING_AGENT_FLAG,
    trendDetectionAgentsPath(projectId ?? ''),
  )

  const { data: agentData, isLoading } = useQuery<BenchmarkingAgentDetail>({
    queryKey: ['benchmarking-agent', projectId, agentId],
    queryFn: async () => {
      if (!projectId || !agentId) throw new Error('missing')
      const agent = await trendService.fetchBenchmarkingAgent(
        projectId,
        agentId,
      )
      return { ...agent, agent_type: 'benchmarking' as const }
    },
    enabled: !!projectId && !!agentId,
    refetchOnWindowFocus: false,
  })

  const agent: BenchmarkingAgentDetail | null = agentData ?? null

  const { runs } = useBenchmarkingAgentRuns(
    trendService,
    projectId,
    agentId ?? null,
  )

  const latestInsightsCount = useMemo(() => {
    const latest = runs.find((r) => r.status === 'completed')
    return latest?.insight_count ?? 0
  }, [runs])

  const { filterOptions } = useFilterOptions(filterService, projectId ?? null)

  useEffect(() => {
    const name = agent?.name ?? null
    setBreadcrumbSubtitle(name)
    return () => setBreadcrumbSubtitle(null)
  }, [agent?.name, setBreadcrumbSubtitle])

  const handleEmailNotificationsToggle = useCallback(
    async (nextValue: boolean): Promise<void> => {
      if (!projectId || !agentId || !agent) return
      trackResearchAgents(ResearchAgentsAction.agent_email_preference_toggled, {
        agent_type: 'benchmarking',
        agent_uuid: agentId,
        agent_name: agent.name,
        enabled: nextValue,
      })
      // Cancel any in-flight refetch so it can't overwrite the optimistic
      // write with the pre-mutation server state and slide the toggle back.
      await queryClient.cancelQueries([
        'benchmarking-agent',
        projectId,
        agentId,
      ])
      const previous = queryClient.getQueryData<BenchmarkingAgentDetail>([
        'benchmarking-agent',
        projectId,
        agentId,
      ])
      queryClient.setQueryData<BenchmarkingAgentDetail | undefined>(
        ['benchmarking-agent', projectId, agentId],
        (old) => (old ? { ...old, email_notifications: nextValue } : old),
      )
      try {
        await trendService.setBenchmarkingAgentEmailPreference(
          projectId,
          agentId,
          nextValue,
        )
        toast.success(nextValue ? 'Emails enabled.' : 'Emails disabled.')
      } catch (cause) {
        handleException(cause, {
          operation: 'agent.benchmarking.email.update',
          disposition: 'failed',
        })

        if (previous) {
          queryClient.setQueryData(
            ['benchmarking-agent', projectId, agentId],
            previous,
          )
        }
        toast.error('Failed to update email preference')
      }
    },
    [projectId, agentId, agent, trendService, queryClient],
  )

  const handleEdit = useCallback(() => {
    if (!projectId || !agentId || !agent || agent.is_default) return
    navigate(
      `${trendDetectionAgentCreatePath(projectId)}?agent_type=benchmarking&agent_id=${agentId}`,
    )
  }, [projectId, agentId, agent, navigate])

  const handleShare = useCallback(() => setIsSharing(true), [])

  const handleToggleActive = useCallback(async () => {
    if (!projectId || !agentId || !agent) return
    const nextEnabled = !agent.is_enabled
    try {
      await trendService.setBenchmarkingAgentEnabled(
        projectId,
        agentId,
        nextEnabled,
      )
      await Promise.all([
        queryClient.invalidateQueries([
          'benchmarking-agent',
          projectId,
          agentId,
        ]),
        queryClient.invalidateQueries(['benchmarking-agents', projectId]),
      ])
      toast.success(nextEnabled ? 'Agent resumed.' : 'Agent paused.')
    } catch (cause) {
      handleException(cause, {
        operation: 'agent.benchmarking.enabled.update',
        disposition: 'failed',
      })

      toast.error('Failed to update agent status.')
    }
  }, [projectId, agentId, agent, trendService, queryClient])

  const confirm = useConfirm()

  const handleDelete = useCallback(() => {
    if (!projectId || !agentId || !agent) return
    void confirm({
      title: `Delete ${agent.name}?`,
      content: 'This cannot be undone.',
      okText: 'Delete',
      danger: true,
      onOk: async () => {
        try {
          await trendService.deleteBenchmarkingAgent(projectId, agentId)
          await queryClient.invalidateQueries([
            'benchmarking-agents',
            projectId,
          ])
          toast.success('Agent deleted.')
          navigate(trendDetectionAgentsPath(projectId))
        } catch (cause) {
          handleException(cause, {
            operation: 'agent.benchmarking.delete',
            disposition: 'failed',
          })

          toast.error('Failed to delete agent.')
        }
      },
    })
  }, [confirm, projectId, agentId, agent, trendService, navigate, queryClient])

  const getInsightsHref = useCallback(
    (recency: InsightsRecency) => {
      if (!projectId || !agentId) return '#'
      return `${trendDetectionFeedPath(projectId)}?agent_uuid=${agentId}&agent_type=benchmarking&insight_recency=${recency}`
    },
    [projectId, agentId],
  )

  const handlers = useMemo(
    () => ({
      onToggleActive: handleToggleActive,
      onShare: handleShare,
      onEdit: handleEdit,
      onDelete: handleDelete,
    }),
    [handleToggleActive, handleShare, handleEdit, handleDelete],
  )

  if (!projectId || !agentId) return null

  if (isLoading) {
    return (
      <Centered>
        <Spin size="large" />
      </Centered>
    )
  }
  if (!agent) {
    return (
      <Centered>
        <Text $size="md">Agent not found.</Text>
      </Centered>
    )
  }

  const focusFilters = agent.config?.focus_group_filters ?? []
  const cohortFilters = agent.config?.cohort_filters ?? []
  const activityRuns: ActivityFeedRun[] = runs
  return (
    <>
      <PageContainer>
        <DetailHeader agent={agent} handlers={handlers} />
        <Body>
          <ActivityFeed
            runs={activityRuns}
            isLoading={false}
            latestInsightsCount={latestInsightsCount}
            getInsightsHref={getInsightsHref}
            onEditFilters={
              agent.is_editor && !agent.is_default ? handlers.onEdit : undefined
            }
          />
          <RightColumn>
            <AgentInfoCard
              header={{ label: 'Agent details' }}
              sections={[
                {
                  icon: <FrameInspectIcon />,
                  title: 'Focus',
                  filters: focusFilters,
                  emptyText: 'All brands in cohort (default)',
                },
                {
                  icon: <ComparisonGroupIcon />,
                  title: 'Comparison Group',
                  filters: cohortFilters,
                  emptyText: 'All brands (default)',
                },
              ]}
              filterOptions={filterOptions}
            />
            <ReskinNotificationsCard
              checked={agent.email_notifications}
              onChange={(checked) =>
                void handleEmailNotificationsToggle(checked)
              }
            />
          </RightColumn>
        </Body>
      </PageContainer>
      <AgentSharingModal
        agentName={agent.name}
        agentUuid={agent.uuid}
        projectId={projectId}
        isEditor={agent.is_editor}
        open={isSharing}
        onClose={() => setIsSharing(false)}
        agentType="benchmarking"
      />
    </>
  )
}

// ── Styled components ────────────────────────────────────────────────────

const PageContainer = styled.div`
  padding: ${spacing[6]}px ${spacing[8]}px;
`

const Body = styled.div`
  display: flex;
  gap: ${spacing[6]}px;
  align-items: flex-start;
`

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]}px;
  flex-shrink: 0;
`

const Centered = styled.div`
  padding: ${spacing[16]}px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 320px;
  color: ${gray[600]};
`
